/**
 * Content for the `/for/:audience` pages.
 *
 * One shape per org type, so a university gets the same page, the same depth
 * and the same sections a league does. When university features land they
 * deepen this entry in place — nothing about the template has to change.
 *
 * `shipping` may only list things that exist in the codebase today. Anything
 * planned goes in `building` where it reads as a roadmap rather than a claim.
 */
export interface AudienceContent {
  /** URL segment. */
  slug: string
  /** `organizations.type` value this audience maps to. */
  orgType: 'organization' | 'league' | 'university'
  /** Short label for the breadcrumb trail — the eyebrow is too long for one. */
  crumb: string
  eyebrow: string
  /** Headline. `\n` becomes a line break — the display cut wraps badly on its own. */
  title: string
  subtext: string
  primaryCta: { label: string; to: string }
  secondaryCta: { label: string; to: string }
  props: { title: string; body: string }[]
  shipping: string[]
  building?: { items: string[]; note: string }
  directory: { heading: string; emptyTitle: string; emptyBody: string; linkLabel: string }
  closing: { title: string; body: string; label: string; to: string }
  seo: { title: string; description: string }
}

const SEASON_OPS = 'Round robin or Swiss into a bracket, a generated schedule, live standings and playoff seeding. Single and double elimination, group stages, best-of per round.'

export const AUDIENCES: Record<string, AudienceContent> = {
  leagues: {
    slug: 'leagues',
    orgType: 'league',
    crumb: 'Leagues',
    eyebrow: 'For league organizers',
    title: 'Stop running\na league in\na spreadsheet.',
    subtext:
      'Scheduling, standings, playoffs and registration screening in one place — so the work that made you start a league is the part you keep doing.',
    primaryCta: { label: 'Create a league', to: '/create-org?type=league' },
    secondaryCta: { label: 'See open competitions', to: '/compete' },
    props: [
      {
        title: 'The season runs itself',
        body: SEASON_OPS,
      },
      {
        title: 'Registration is screened before you see it',
        body: 'Set a rank range and a Trust Score floor. Teams that fail either never reach your approval queue, and every player arrives with linked game accounts and competitive history attached.',
      },
      {
        title: 'Your league, your brand',
        body: 'A public page in your own colours and logo, with standings, schedule, rosters and results. Carousel stays out of the way apart from a footer line.',
      },
    ],
    shipping: [
      'Season scheduling & standings',
      'Swiss, groups, single & double elim',
      'Rank-range + Trust Score eligibility',
      'Divisions & multiple concurrent seasons',
      'White-label league page',
      'Competition templates',
      'Walk-up registration for LAN events',
      'Riot-verified match results',
    ],
    directory: {
      heading: 'Leagues on Carousel',
      emptyTitle: 'Your league here',
      emptyBody: 'Setup takes about five minutes and goes to Carousel staff for approval.',
      linkLabel: 'All leagues in the directory',
    },
    closing: {
      title: 'Run your first season.',
      body: 'Create the league, set the divisions, open registration. You can have teams signing up this week.',
      label: 'Create a league',
      to: '/create-org?type=league',
    },
    seo: {
      title: 'For league organizers',
      description:
        'Run an amateur esports league without the spreadsheet: scheduling, standings, playoffs, and registration screened by verified player identity.',
    },
  },

  clubs: {
    slug: 'clubs',
    orgType: 'organization',
    crumb: 'Clubs',
    eyebrow: 'For clubs & communities',
    title: 'Give your\nclub a home\nthat lasts.',
    subtext:
      'Rosters across games and skill brackets, a public page people can actually find, and a record of what your teams have done that outlives any one Discord server.',
    primaryCta: { label: 'Create a club', to: '/create-org?type=organization' },
    secondaryCta: { label: 'Browse clubs', to: '/orgs?type=organization' },
    props: [
      {
        title: 'Rosters that stay straight',
        body: 'Multiple teams per game and per Elo bracket, with substitutes, roles and invitations. Who is on which roster stops being a pinned message.',
      },
      {
        title: 'Players can find you',
        body: 'Your club appears in the directory with its games, its open spots and its record. Free agents apply to a roster instead of asking around.',
      },
      {
        title: 'Compete anywhere',
        body: 'Enter your teams in any league or tournament on Carousel. Eligibility, rank ranges and Trust Score checks are handled at registration.',
      },
    ],
    shipping: [
      'Teams across games and Elo brackets',
      'Roster management & substitutes',
      'Invitations & join codes',
      'White-label club page',
      'Free-agent pool & recruiting',
      'Register for any league or tournament',
      'Verified player identity on every roster',
      'News & media for your page',
    ],
    directory: {
      heading: 'Clubs on Carousel',
      emptyTitle: 'Your club here',
      emptyBody: 'Clubs go live immediately — no approval step, no waiting.',
      linkLabel: 'All clubs in the directory',
    },
    closing: {
      title: 'Set up your club.',
      body: 'Name it, add your games, invite your players. Clubs go live the moment you finish.',
      label: 'Create a club',
      to: '/create-org?type=organization',
    },
    seo: {
      title: 'For clubs & communities',
      description:
        'Run an esports club on Carousel: rosters across games and skill brackets, a public page, recruiting, and entry into any league or tournament.',
    },
  },

  universities: {
    slug: 'universities',
    orgType: 'university',
    crumb: 'Universities',
    eyebrow: 'For campus programs',
    title: 'Your campus\nalready has\na league.',
    subtext:
      'It runs in a Discord server, it is scored in a spreadsheet, and it disappears when the student who built it graduates. Carousel gives it a real home — and keeps it there next year.',
    primaryCta: { label: 'Register your program', to: '/create-org?type=university' },
    secondaryCta: { label: 'Browse programs', to: '/orgs?type=university' },
    props: [
      {
        title: 'Run the season, not the spreadsheet',
        body: SEASON_OPS,
      },
      {
        title: 'Nobody smurfs into your bracket',
        body: 'Every player carries a verified identity — linked game accounts, phone, competitive history. Set a rank range and a Trust Score floor and alt accounts never reach registration.',
      },
      {
        title: 'The program outlives the student',
        body: "Rosters, results and player history live on the program's page, not in a graduating senior's Discord. Hand over admin, keep years of record.",
      },
    ],
    shipping: [
      'Season scheduling & standings',
      'Swiss, groups, single & double elim',
      'Rank-range + Trust Score eligibility',
      'Roster management & substitutes',
      'White-label program page',
      'Free-agent pool & recruiting',
      'Walk-up registration for LAN events',
      'Riot-verified match results',
    ],
    // Listed as roadmap rather than features, because none of it exists yet.
    // The live page used to claim all three in its hero.
    building: {
      items: [
        'LMS sync (Canvas, Blackboard)',
        'Academic eligibility & GPA checks',
        'Scholarship pipeline tooling',
      ],
      note: 'Listed here rather than on the feature list, because they are not built yet — and pilot programs get to order them.',
    },
    directory: {
      heading: 'Programs on Carousel',
      emptyTitle: 'Your program here',
      emptyBody: 'Registration takes about five minutes and goes to Carousel staff for approval.',
      linkLabel: 'All universities in the directory',
    },
    closing: {
      title: 'Free while we build it with you.',
      body: 'Pilot programs pay nothing and get direct input on what ships next. Register the program, run one season, tell us what broke.',
      label: 'Register your program',
      to: '/create-org?type=university',
    },
    seo: {
      title: 'For campus programs',
      description:
        'Run your university esports program on Carousel: season scheduling, standings, playoffs, verified player identity, and a program page that outlasts every graduating class.',
    },
  },
}

export const AUDIENCE_SLUGS = Object.keys(AUDIENCES)
